import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ReceiptLong as ReceiptIcon,
  Print as PrintIcon,
  AccountBalanceWallet as WalletIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchPaymentsForPayslip, fetchPayslipById } from "./api/teacherHrApi";
import "./teacherHr.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ArrowDown, ArrowUp, Check, IndianRupeeIcon } from "lucide-react";

const currency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(val || 0));

const statusChip = (status) => {
  const map = {
    paid: { color: "success", variant: "filled" },
    finalized: { color: "info", variant: "filled" },
    draft: { color: "default", variant: "outlined" },
    pending: { color: "warning", variant: "filled" },
  };
  const cfg = map[status?.toLowerCase()] || {
    color: "default",
    variant: "outlined",
  };
  return (
    <Chip size="small" label={status} color={cfg.color} variant={cfg.variant} />
  );
};

function SectionCard({ title, icon, children, sx }) {
  return (
    <Card elevation={1} className="shadow-sm border rounded-3 mb-3" sx={sx}>
      <CardContent className="p-4">
        <Box className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
          {icon}
          <Typography variant="h6" className="fw-bold m-0">
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function LineTable({ title, lines, category }) {
  const filtered = (lines || []).filter((l) => l.category === category);
  if (filtered.length === 0) return null;
  return (
    <Box mb={3}>
      <Typography
        variant="subtitle2"
        className="text-uppercase text-muted fw-bold mb-2"
        fontSize="0.75rem"
      >
        {title}
      </Typography>
      <TableContainer
        className="rounded-2 border"
        sx={{ borderColor: "divider" }}
      >
        <Table size="small" className="tea-table">
          <TableHead>
            <TableRow>
              <TableCell className="fw-bold">Code</TableCell>
              <TableCell className="fw-bold">Name</TableCell>
              <TableCell align="right" className="fw-bold">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((l, i) => (
              <TableRow key={`${l.code}-${i}`} hover>
                <TableCell>
                  <code className="bg-light px-2 py-1 rounded text-dark">
                    {l.code}
                  </code>
                </TableCell>
                <TableCell>{l.name}</TableCell>
                <TableCell align="right" className="fw-medium">
                  {currency(l.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function PayslipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const teacherInfo = JSON.parse(
    localStorage.getItem("UserInformation") || "{}",
  );

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [ps, pay] = await Promise.all([
        fetchPayslipById(id),
        fetchPaymentsForPayslip(id),
      ]);
      setSlip(ps.data?.data || null);
      const payData = pay.data?.data;
      setPayments(Array.isArray(payData) ? payData : []);
    } catch (e) {
      toast.error(
        e.response?.data?.message || e.message || "Failed to load payslip",
      );
      setSlip(null);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Container className="py-4">
        <Box className="d-flex align-items-center gap-3 justify-content-center py-5">
          <ReceiptIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" className="fw-bold text-muted">
            Loading payslip…
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!slip) {
    return (
      <Container className="py-4">
        <Card className="shadow-sm border rounded-3 text-center p-5">
          <ReceiptIcon sx={{ fontSize: 64, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" className="fw-bold mb-2">
            Payslip not found
          </Typography>
          <Typography color="text.secondary" className="mb-3">
            This payslip is unavailable or you do not have access to it.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/tea-payslips")}
            className="text-capitalize fw-semibold"
          >
            Back to Payslips
          </Button>
        </Card>
      </Container>
    );
  }

  const downloadPayslipPDF = (payslip) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // =========================
    // Color Palette & Theme
    // =========================
    const colors = {
      primary: [41, 128, 185], // Professional Blue
      primaryLight: [236, 240, 241], // Light Blue BG
      success: [39, 174, 96], // Green
      danger: [231, 76, 60], // Red
      warning: [243, 156, 18], // Orange
      dark: [44, 62, 80], // Dark Blue-Gray
      gray: [127, 140, 141], // Gray
      lightGray: [189, 195, 199], // Light Gray
      white: [255, 255, 255],
      black: [0, 0, 0],
    };

    // =========================
    // Helpers
    // =========================
    const formatCurrency = (amount) =>
      `Rs. ${Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`;

    const formatDate = (date) =>
      new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    // =========================
    // Background Design
    // =========================
    // Subtle top accent bar
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 8, "F");

    // Light background pattern
    doc.setFillColor(250, 250, 252);
    doc.rect(0, 8, pageWidth, pageHeight - 8, "F");

    // =========================
    // Header Section with Logo Area
    // =========================
    let currentY = 20;

    // Company header box
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.lightGray);
    doc.roundedRect(margin, currentY, contentWidth, 28, 3, 3, "FD");

    // Left: Title
    doc.setFontSize(22);
    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "bold");
    doc.text(`PAYSLIP`, margin + 5, currentY + 12);

    doc.setFontSize(11);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${getMonthName(payslip.month)} ${payslip.year}`,
      margin + 5,
      currentY + 20,
    );

    // Right: Status Badge
    const statusColors = {
      paid: colors.success,
      pending: colors.warning,
      processing: colors.primary,
      failed: colors.danger,
    };
    const statusColor =
      statusColors[payslip.status?.toLowerCase()] || colors.gray;

    const statusText = payslip.status?.toUpperCase() || "UNKNOWN";
    const statusWidth = doc.getTextWidth(statusText) + 16;
    const statusX = pageWidth - margin - statusWidth - 4;

    // Status badge background
    doc.setFillColor(...statusColor);
    doc.setDrawColor(...statusColor);
    doc.roundedRect(statusX, currentY + 6, statusWidth, 14, 7, 7, "FD");

    doc.setFontSize(9);
    doc.setTextColor(...colors.white);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, statusX + statusWidth / 2, currentY + 15, {
      align: "center",
    });

    // Generated date
    doc.setFontSize(9);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Generated: ${formatDate(payslip.generatedAt)}`,
      pageWidth - margin - 4,
      currentY + 24,
      { align: "right" },
    );

    currentY += 36;

    // =========================
    // Employee Info Card
    // =========================
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.lightGray);
    doc.roundedRect(margin, currentY, contentWidth, 30, 3, 3, "FD");

    doc.setFontSize(10);
    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "bold");
    doc.text("EMPLOYEE DETAILS", margin + 5, currentY + 10);

    // Divider line
    doc.setDrawColor(...colors.primaryLight);
    doc.line(
      margin + 5,
      currentY + 13,
      margin + contentWidth - 8,
      currentY + 13,
    );

    // Employee info grid (2 columns)
    const colWidth = (contentWidth - 16) / 2;
    const infoItems = [
      { label: "Employee ID", value: teacherInfo.employee_id || "N/A" },
      { label: "Designation", value: teacherInfo.position || "N/A" },
      { label: "Employee Name", value: teacherInfo.name || "N/A" },
      { label: "Employee Code", value: teacherInfo.code || "N/A" },
    ];

    doc.setFontSize(9);
    infoItems.forEach((item, index) => {
      const x = margin + 5 + (index % 2) * colWidth;
      const y = currentY + 20 + Math.floor(index / 2) * 8;

      doc.setTextColor(...colors.gray);
      doc.setFont("helvetica", "normal");
      doc.text(`${item.label}:`, x, y);

      doc.setTextColor(...colors.dark);
      doc.setFont("helvetica", "bold");
      doc.text(item.value, x + 35, y);
    });

    currentY += 38;

    // =========================
    // Summary Cards (Modern Grid)
    // =========================
    const cardHeight = 32;
    const cardGap = 4;
    const cardsPerRow = 2;
    const cardWidth = (contentWidth - cardGap) / cardsPerRow;

    const summaryCards = [
      {
        title: "Gross Earnings",
        value: formatCurrency(payslip.gross),
        icon: "GE",
        color: colors.success,
        bgColor: [232, 245, 233],
      },
      {
        title: "Total Deductions",
        value: formatCurrency(payslip.totalDeductions),
        icon: "TD",
        color: colors.danger,
        bgColor: [255, 235, 238],
      },
      {
        title: "Net Payable",
        value: formatCurrency(payslip.net),
        icon: "NP",
        color: colors.primary,
        bgColor: [227, 242, 253],
      },
      {
        title: "Amount Paid",
        value: formatCurrency(payslip.paidAmount),
        icon: "AP",
        color: colors.success,
        bgColor: [232, 245, 233],
      },
    ];

    summaryCards.forEach((card, index) => {
      const col = index % cardsPerRow;
      const row = Math.floor(index / cardsPerRow);
      const x = margin + col * (cardWidth + cardGap);
      const y = currentY + row * (cardHeight + cardGap);

      // Card background
      doc.setFillColor(...card.bgColor);
      doc.setDrawColor(...card.color);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "FD");

      // Left accent bar
      doc.setFillColor(...card.color);
      doc.rect(x, y, 3, cardHeight, "F");

      // Icon circle
      doc.setFillColor(...colors.white);
      doc.setDrawColor(...card.color);
      doc.circle(x + 14, y + 12, 5.5, "FD");

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(card.icon, x + 14, y + 13, {
        align: "center",
      });

      // Title
      doc.setFontSize(9);
      doc.setTextColor(...colors.gray);
      doc.setFont("helvetica", "normal");
      doc.text(card.title, x + 24, y + 10);

      // Value
      doc.setFontSize(14);
      doc.setTextColor(...card.color);
      doc.setFont("helvetica", "bold");
      doc.text(card.value, x + 24, y + 22);
    });

    currentY +=
      Math.ceil(summaryCards.length / cardsPerRow) * (cardHeight + cardGap) + 6;

    // =========================
    // Salary Breakdown Section
    // =========================
    // Section header with accent
    doc.setFillColor(...colors.primary);
    doc.rect(margin, currentY, 4, 18, "F");

    doc.setFontSize(14);
    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "bold");
    doc.text("Salary Breakdown", margin + 10, currentY + 12);

    currentY += 22;

    // Modern Table
    autoTable(doc, {
      startY: currentY,
      head: [["Component", "Code", "Category", "Amount (Rs)"]],
      body: payslip.lines.map((line) => [
        line.name,
        line.code,
        line.category,
        { content: formatCurrency(line.amount), styles: { halign: "right" } },
      ]),
      theme: "plain",
      styles: {
        fontSize: 10,
        cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
        font: "helvetica",
        textColor: colors.dark,
        lineColor: colors.lightGray,
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontSize: 10,
        fontStyle: "bold",
        cellPadding: { top: 8, right: 8, bottom: 8, left: 8 },
        lineWidth: 0,
      },
      alternateRowStyles: {
        fillColor: colors.primaryLight,
      },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: "auto" },
        1: { cellWidth: 30, halign: "center" },
        2: { cellWidth: 40, halign: "center" },
        3: { cellWidth: 45, halign: "right", fontStyle: "bold" },
      },
      didDrawCell: (data) => {
        // Add subtle left border for earnings/deductions
        if (data.section === "body" && data.column.index === 2) {
          const category = data.cell.raw;
          if (category === "Earning" || category === "Deduction") {
            doc.setDrawColor(
              category === "Earning" ? colors.success : colors.danger,
            );
            doc.setLineWidth(2);
            doc.line(
              data.cell.x,
              data.cell.y + 1,
              data.cell.x,
              data.cell.y + data.cell.height - 1,
            );
          }
        }
      },
    });

    const tableEndY = doc.lastAutoTable.finalY + 10;

    // =========================
    // Calculation Summary Box
    // =========================
    const calcBoxY = tableEndY;
    const calcBoxHeight = 45;

    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.lightGray);
    doc.roundedRect(margin, calcBoxY, contentWidth, calcBoxHeight, 3, 3, "FD");

    // Left side: Calculation steps
    let calcY = calcBoxY + 12;

    doc.setFontSize(10);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "normal");
    doc.text("Gross Earnings", margin + 7, calcY);
    doc.text(formatCurrency(payslip.gross), margin + 80, calcY, {
      align: "right",
    });

    calcY += 10;
    doc.setTextColor(...colors.danger);
    doc.text("Total Deductions", margin + 7, calcY);
    doc.text(
      `-${formatCurrency(payslip.totalDeductions)}`,
      margin + 80,
      calcY,
      { align: "right" },
    );

    // Divider
    doc.setDrawColor(...colors.lightGray);
    doc.line(margin + 10, calcY + 4, margin + 60, calcY + 4);

    calcY += 14;
    doc.setFontSize(12);
    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "bold");
    doc.text("Net Payable", margin + 10, calcY);
    doc.text(formatCurrency(payslip.net), margin + 80, calcY, {
      align: "right",
    });

    // Right side: Highlighted Net Pay
    const highlightX = pageWidth - margin - 70;
    doc.setFillColor(...colors.primaryLight);
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(1);
    doc.roundedRect(highlightX, calcBoxY + 8, 66, 30, 4, 4, "FD");

    doc.setFontSize(9);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "normal");
    doc.text("NET PAY", highlightX + 33, calcBoxY + 18, { align: "center" });

    doc.setFontSize(16);
    doc.setTextColor(...colors.primary);
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(payslip.net), highlightX + 33, calcBoxY + 32, {
      align: "center",
    });

    // =========================
    // Payment Details (if available)
    // =========================
    if (payslip.paymentDate || payslip.paymentMethod) {
      const payY = calcBoxY + calcBoxHeight + 8;

      doc.setFillColor(...colors.white);
      doc.setDrawColor(...colors.lightGray);
      doc.roundedRect(margin, payY, contentWidth, 22, 3, 3, "FD");

      doc.setFontSize(10);
      doc.setTextColor(...colors.dark);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Details", margin + 8, payY + 10);

      doc.setFontSize(9);
      doc.setTextColor(...colors.gray);
      doc.setFont("helvetica", "normal");

      let detailX = margin + 8;
      if (payslip.paymentDate) {
        doc.text(
          `Paid On: ${formatDate(payslip.paymentDate)}`,
          detailX,
          payY + 18,
        );
        detailX += 60;
      }
      if (payslip.paymentMethod) {
        doc.text(`Method: ${payslip.paymentMethod}`, detailX, payY + 18);
      }
    }

    // =========================
    // Footer
    // =========================
    // Bottom accent bar
    doc.setFillColor(...colors.primary);
    doc.rect(0, pageHeight - 6, pageWidth, 6, "F");

    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(...colors.gray);
    doc.setFont("helvetica", "normal");

    const footerY = pageHeight - 14;

    if (payslip.companyName) {
      doc.setFontSize(9);
      doc.setTextColor(...colors.dark);
      doc.setFont("helvetica", "bold");
      doc.text(payslip.companyName, pageWidth / 2, footerY - 6, {
        align: "center",
      });
    }

    // =========================
    // Download
    // =========================
    const fileName = `Payslip_${getMonthName(payslip.month)}_${payslip.year}_${payslip.employeeId || "Employee"}.pdf`;
    doc.save(fileName);
  };

  // Helper function for month names
  function getMonthName(monthNum) {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[Number(monthNum) - 1] || monthNum;
  }

  const lines = slip.lines || [];
  const adhoc = slip.adhoc || [];
  const lop = slip.lop || {};
  console.log("lines", lines);
  console.log("slip", slip);

  return (
    <div
      className="container-2xl p-3 orange-scrollBar"
      style={{ maxHeight: 600, overflow: "auto" }}
    >
      {/* Top Bar */}
      <Box className="d-flex align-items-center justify-content-between mb-2">
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/tea-payslips")}
          className="text-capitalize fw-semibold"
        >
          Back to list
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => downloadPayslipPDF(slip)}
          className="text-capitalize fw-semibold"
        >
          Download PDF
        </Button>
      </Box>

      {/* Header */}
      <Box className="mb-4">
        <Box className="d-flex align-items-center gap-3 mb-2">
          <ReceiptIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" component="h3" className="fw-bold m-0">
              Payslip {slip.month}/{slip.year}
            </Typography>
            <Box className="d-flex align-items-center gap-2 mt-1">
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Period: {slip.month}/{slip.year}
              </Typography>
              <Divider orientation="vertical" flexItem className="mx-1" />
              {statusChip(slip.status)}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} className="mb-4">
        {[
          {
            label: "Gross Earnings",
            value: currency(slip.gross),
            color: "text-success",
            icon: <WalletIcon fontSize="small" />,
          },
          {
            label: "Total Deductions",
            value: currency(slip.totalDeductions),
            color: "text-danger",
            icon: <InfoIcon fontSize="small" />,
          },
          {
            label: "Net Pay",
            value: currency(slip.net),
            color: "text-primary",
            icon: <ReceiptIcon fontSize="small" />,
          },
          {
            label: "Amount Paid",
            value: currency(slip.paidAmount),
            color: "text-success",
            icon: <WalletIcon fontSize="small" />,
          },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Card className="shadow-sm border-0 rounded-3 h-100">
              <CardContent className="p-3">
                <Box className="d-flex align-items-center gap-2 mb-1 text-muted">
                  {s.icon}
                  <Typography
                    variant="caption"
                    className="text-uppercase fw-bold"
                  >
                    {s.label}
                  </Typography>
                </Box>
                <Typography variant="h6" className={`fw-bold m-0 ${s.color}`}>
                  {s.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Earnings & Deductions */}
      <SectionCard
        title="Salary Breakdown"
        icon={<ReceiptIcon color="primary" fontSize="small" />}
      >
        <LineTable title="Earnings" lines={lines} category="earning" />
        <LineTable title="Deductions" lines={lines} category="deduction" />

        {adhoc.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              className="text-uppercase text-muted fw-bold mb-2"
              fontSize="0.75rem"
            >
              Adjustments (Adhoc)
            </Typography>
            <TableContainer
              className="rounded-2 border"
              sx={{ borderColor: "divider" }}
            >
              <Table size="small" className="tea-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="fw-bold">Type</TableCell>
                    <TableCell className="fw-bold">Name</TableCell>
                    <TableCell align="right" className="fw-bold">
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adhoc.map((a) => (
                    <TableRow key={a._id || a.name} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={a.category}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{a.name}</TableCell>
                      <TableCell align="right" className="fw-medium">
                        {currency(a.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {(lop.daysApplied != null && lop.daysApplied > 0) ||
        (lop.amount != null && lop.amount > 0) ? (
          <Box
            className="rounded-3 p-3 mb-2"
            sx={{
              backgroundColor: "error.lighter",
              border: 1,
              borderColor: "error.light",
            }}
          >
            <Typography
              variant="subtitle2"
              className="fw-bold text-danger mb-1"
            >
              Loss of Pay (LoP)
            </Typography>
            <Typography variant="body2" className="text-danger">
              Days applied: <strong>{lop.daysApplied ?? 0}</strong> · Amount
              deducted: <strong>{currency(lop.amount ?? 0)}</strong>
            </Typography>
          </Box>
        ) : null}
      </SectionCard>

      {/* Totals Box */}
      <Card
        className="shadow-sm border rounded-3 mb-4"
        sx={{ backgroundColor: "#f8fafc" }}
      >
        <CardContent className="p-4">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className="d-flex justify-content-between mb-2">
                <Typography variant="body2" color="text.secondary">
                  Gross Earnings
                </Typography>
                <Typography variant="body2" className="fw-bold">
                  {currency(slip.gross)}
                </Typography>
              </Box>
              <Box className="d-flex justify-content-between mb-2">
                <Typography variant="body2" color="text.secondary">
                  Total Deductions
                </Typography>
                <Typography variant="body2" className="fw-bold text-danger">
                  {currency(slip.totalDeductions)}
                </Typography>
              </Box>
              <Divider className="my-2" />
              <Box className="d-flex justify-content-between">
                <Typography variant="subtitle1" className="fw-bold">
                  Net Payable
                </Typography>
                <Typography
                  variant="subtitle1"
                  className="fw-bold text-primary"
                >
                  {currency(slip.net)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="d-flex justify-content-between mb-2">
                <Typography variant="body2" color="text.secondary">
                  Amount Paid
                </Typography>
                <Typography variant="body2" className="fw-bold text-success">
                  {currency(slip.paidAmount)}
                </Typography>
              </Box>
              <Box className="d-flex justify-content-between">
                <Typography variant="body2" color="text.secondary">
                  Balance
                </Typography>
                <Typography variant="body2" className="fw-bold">
                  {currency(
                    Number(slip.net || 0) - Number(slip.paidAmount || 0),
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payments */}
      <SectionCard
        title="Payment History"
        icon={<WalletIcon color="primary" fontSize="small" />}
      >
        {payments.length === 0 ? (
          <Box className="text-center py-4">
            <WalletIcon
              sx={{ fontSize: 48, color: "action.disabled", mb: 1 }}
            />
            <Typography color="text.secondary" variant="body1">
              No payments recorded for this payslip yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            className="rounded-2 border"
            sx={{ borderColor: "divider" }}
          >
            <Table size="small" className="tea-table">
              <TableHead>
                <TableRow>
                  <TableCell className="fw-bold">Amount</TableCell>
                  <TableCell className="fw-bold">Mode</TableCell>
                  <TableCell className="fw-bold">Reference</TableCell>
                  <TableCell className="fw-bold">Date</TableCell>
                  <TableCell className="fw-bold" align="right">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell className="fw-medium">
                      {currency(p.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={p.mode} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <code className="bg-light px-2 py-1 rounded">
                        {p.reference || "—"}
                      </code>
                    </TableCell>
                    <TableCell>
                      {p.paidAt
                        ? new Date(p.paidAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label="Completed" color="success" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </div>
  );
}
